
// Shared with flightAnchor.ts — see ScrollToBookableOptions there for the
// behavior / sectionFallback semantics.
import type { ScrollToBookableOptions } from "./flightAnchor";

// Attribute on each bookable hotel-card wrapper; its value = hotelAnchorKey(name).
export const HOTEL_ANCHOR_ATTR = "data-hotel-anchor";
// Attribute on the bookable results section container — the no-hotel-match fallback.
export const HOTEL_SECTION_ATTR = "data-hotel-section";

// Normalize a hotel name to a stable match key: lowercase, punctuation → space,
// collapse whitespace. Same transform on the stream side and the bookable side.
export function hotelAnchorKey(name?: string | null): string {
  return (name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Scroll the page to the bookable card for `name`. Exact key match first, then a
// prefix match (the stream may carry a shorter name than the bookable card —
// e.g. "Niepce Paris Hotel" vs "Niepce Paris Hotel, Curio Collection by Hilton").
// If no hotel matches, fall back to the results section so the click still lands
// the user in the hotel list rather than doing nothing. Returns true if it
// scrolled to anything. The scroll-restore path passes { behavior:"auto",
// sectionFallback:false } so a stale/mismatched anchor no-ops instead of yanking
// the user to an unrelated section.
export function scrollToBookableHotel(
  name?: string | null,
  opts?: ScrollToBookableOptions
): boolean {
  const behavior = opts?.behavior ?? "smooth";
  const sectionFallback = opts?.sectionFallback ?? true;
  const key = hotelAnchorKey(name);
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>(`[${HOTEL_ANCHOR_ATTR}]`)
  );

  let target: HTMLElement | undefined;
  if (key) {
    target =
      nodes.find((n) => n.getAttribute(HOTEL_ANCHOR_ATTR) === key) ??
      nodes.find((n) => {
        const k = n.getAttribute(HOTEL_ANCHOR_ATTR) ?? "";
        return !!k && (k.startsWith(key) || key.startsWith(k));
      });
  }

  // No per-hotel match → land in the results section anyway (unless the caller
  // opted out, e.g. the scroll-restore path).
  const el =
    target ??
    (sectionFallback ? document.querySelector<HTMLElement>(`[${HOTEL_SECTION_ATTR}]`) : null) ??
    null;
  if (!el) return false;

  // Repo idiom (e.g. CityTabs.tsx) — scrollIntoView walks up to the real scroll
  // ancestor itself, which covers the iOS body-scroll quirk noted in CLAUDE.md.
  el.scrollIntoView({ behavior, block: target ? "center" : "start" });
  return true;
}
